import { ReactNode } from 'react'
import { Card, CardContent } from '@/components/ui/Card/Card'
import { VideoSetupPanel } from '@/components/organisms/VideoSetupPanel/VideoSetupPanel'
import { AppointmentInfoSection } from '@/components/molecules/AppointmentInfoSection'
import { cn } from '@/utils/helpers'
import type { VideoState } from '@hooks/useVideoState'
import { TimeStatus } from '@/models/generated/graphql'

type Device = {
  id: string
  label: string
}

type WaitingRoomCardProps = {
  // Video Setup Props
  isVideoActive: boolean
  isMicrophoneActive: boolean
  noCameraAvailable: boolean
  noMicrophoneAvailable: boolean
  onToggleVideo: () => void
  onToggleMicrophone: () => void
  videoInputs: Device[]
  audioInputs: Device[]
  audioOutputs: Device[]
  selectedVideoInput?: string
  selectedAudioInput?: string
  selectedAudioOutput?: string
  onVideoInputChange: (id: string) => void
  onAudioInputChange: (id: string) => void
  onAudioOutputChange: (id: string) => void
  audioLevel?: number
  videoState?: VideoState
  previewComponent?: ReactNode
  isCameraPermissionDenied?: boolean
  isMicrophonePermissionDenied?: boolean

  // Appointment Info Props
  participantName: string
  participantAvatar?: string
  scheduledDate: string
  isParticipantPresent: boolean
  conversationId: string
  timeStatus: TimeStatus
  onJoinMeeting: () => void
  isJoining?: boolean

  className?: string
}

export const WaitingRoomCard = ({
  // Video Setup Props
  isVideoActive,
  isMicrophoneActive,
  noCameraAvailable,
  noMicrophoneAvailable,
  onToggleVideo,
  onToggleMicrophone,
  videoInputs,
  audioInputs,
  audioOutputs,
  selectedVideoInput,
  selectedAudioInput,
  selectedAudioOutput,
  onVideoInputChange,
  onAudioInputChange,
  onAudioOutputChange,
  audioLevel = 0,
  videoState = 'inactive',
  previewComponent,
  isCameraPermissionDenied = false,
  isMicrophonePermissionDenied = false,

  // Appointment Info Props
  participantName,
  participantAvatar,
  scheduledDate,
  isParticipantPresent,
  conversationId,
  timeStatus,
  onJoinMeeting,
  isJoining,

  className
}: WaitingRoomCardProps) => {
  return (
    <Card className={cn('shadow-none flex-1 lg:flex-none rounded-none lg:rounded-xl', className)}>
      <CardContent className="h-full p-6">
        <div className="h-full lg:h-auto grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Left: Video Setup */}
          <div className="flex flex-col">
            <VideoSetupPanel
              isVideoActive={isVideoActive}
              isMicrophoneActive={isMicrophoneActive}
              noCameraAvailable={noCameraAvailable}
              noMicrophoneAvailable={noMicrophoneAvailable}
              onToggleVideo={onToggleVideo}
              onToggleMicrophone={onToggleMicrophone}
              videoInputs={videoInputs}
              audioInputs={audioInputs}
              audioOutputs={audioOutputs}
              selectedVideoInput={selectedVideoInput}
              selectedAudioInput={selectedAudioInput}
              selectedAudioOutput={selectedAudioOutput}
              onVideoInputChange={onVideoInputChange}
              onAudioInputChange={onAudioInputChange}
              onAudioOutputChange={onAudioOutputChange}
              audioLevel={audioLevel}
              videoState={videoState}
              previewComponent={previewComponent}
              isCameraPermissionDenied={isCameraPermissionDenied}
              isMicrophonePermissionDenied={isMicrophonePermissionDenied}
              conversationId={conversationId}
            />
          </div>

          {/* Right: Appointment Info */}
          <div className="flex flex-col justify-center">
            <AppointmentInfoSection
              participantName={participantName}
              participantAvatar={participantAvatar}
              scheduledDate={scheduledDate}
              isParticipantPresent={isParticipantPresent}
              conversationId={conversationId}
              timeStatus={timeStatus}
              onJoinMeeting={onJoinMeeting}
              isJoining={isJoining}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
